import React, { useEffect } from "react";
import { BrowserRouter, Route, Switch, Redirect } from "react-router-dom";
import {
  QueryClient,
  QueryClientProvider,
} from 'react-query'

import { session } from "./load-config";

import DomainRepository from "./domain-repository/DomainRepository";
import GroupsImagesRepository from "./domain-repository/GroupsImagesRepository";

import ErrorBoundry from "./components/ErrorBoundry";
import { TermsConditionsPage } from "./components/legal-information-section/LegalInformation";
import AlertModalContainer from "./components/alert-modal-wraper/AlertModalContainer";
import ValidateSessionId from "./components/ValidateSessionId";
import TrackingPage from "./components/TrackingPage";

import StartPage from "./pages/start/StartPage";
import SelectLanguagePage from "./pages/select-language/SelectLanguagePage";
import GetReadyPage from "./pages/get-ready/GetReadyPage";
import InputReservationReferencePage from "./pages/input-reservation-ref/InputReservationReferencePage";
import InputReservationReferenceErrorPage from "./pages/input-reservation-ref/InputReservationReferenceErrorPage";
import ReviewReservationPage from "./pages/review-reservation/ReviewReservationPage";
import ReviewReservationExtrasPage from "./pages/review-reservation/ReviewReservationExtrasPage";

import DriverStep1Page from "./pages/driver/DriverStep1Page";
import DriverStep2Page from "./pages/driver/DriverStep2Page";
import DriverStep3Page from "./pages/driver/DriverStep3Page";
import DriverInfoReviewPage from "./pages/driver/DriverInfoReviewPage";
import DriverInfoInvalidAddressPage from "./pages/driver/DriverInfoInvalidAddressPage";
import AdditionalDriverStepPage from "./pages/driver/AdditionalDriverStepPage";
import AdditionalDriverInfoReview from "./pages/driver/AdditionalDriverInfoReview";

import ComfortExtrasPage from "./pages/comfort/ComfortExtrasPage";
import SingleExtraPage from "./pages/comfort/SingleExtraPage";

import ReviewFinalReservationPage from "./pages/review-final-reservation/ReviewFinalReservationPage";
import ReviewFinalReservationExtrasPage from "./pages/review-final-reservation/ReviewFinalReservationExtrasPage";

import ThankYouPage from "./pages/thank-you/ThankYouPage";

import SelectPickupDetailsPage from "./pages/select-pickup-details/SelectPickupDetailsPage";

import AutoSearchReservationPage from "./pages/auto-search-reservation/AutoSearchReservationPage";

import SecondTryCheckInPage from "./pages/second-try-check-in/SecondTryCheckInPage";

import BabySeatsPage from "./pages/baby-seats/BabySeatsPage";

import UpsellingPage from "./pages/upselling/UpsellingPage";

import SafetyPacksPage from "./pages/safety-packs/SafetyPacksPage";
import SafetyPackDetailedPage from "./pages/safety-packs/SafetyPackDetailed";

import QuestionPage from "./pages/questions/QuestionPage";

import RatingPage from "./pages/rating/RatingPage";

import ExclusiveReservationQuestionPage from "./pages/exclusive-reservation/ExclusiveReservationQuestionPage";

import ReviewRentalAgreementPage from "./pages/review-rental-agreement/ReviewRentalAgreementPage";

import PushSalesPage, {
  PATH as PUSH_SALES_PATH,
} from "./pages/push-sales/PushSalesPage";
import Template1Page from "./pages/templates/Template1";

import PaymentsPage from "./pages/payments/PaymentsPage";
import PaymentsErrorPage from "./pages/payments/PaymentsErrorPage";
import PaymentsSuccessPage from "./pages/payments/PaymentsSuccessPage";
import PaymentsReminderPage from "./pages/payments/PaymentsReminderPage";
import { KeylessPage } from "./pages/keyless/KeylessPage";
import { KeylessSelfCheckoutConfirmPage } from './pages/keyless/KeylessSelfCheckout'
import ExpiredRentalAgreementPage from "./pages/review-rental-agreement/ExpiredRentalAgreementPage";
import { TestPage } from "./TestPage";
import { StartSessionPage } from "./pages/input-reservation-ref/StartSessionPage";
import { ConventionPage } from "./pages/keyless/convention/ConventionPage";
import { DeviceDetector } from "./components/DeviceDetector";

const queryClient = new QueryClient()

function useSaveSessionEndOnCloseApp() {
  // only works on Desktop...
  useEffect(() => {
    function saveSessionEnd() {
      if (session.getSessionId()) {
        navigator.sendBeacon(
          `${process.env.REACT_APP_WEB_API_URL}/session-end`
        );
      }
    }

    window.addEventListener("beforeunload", saveSessionEnd);

    return () => {
      window.removeEventListener("beforeunload", saveSessionEnd);
    };
  }, []);
}

function Root() {
  useSaveSessionEndOnCloseApp();

  const queryStringParams = window.location.search;

  return (
    <DeviceDetector>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundry>
          <GroupsImagesRepository>
            <DomainRepository>
              <BrowserRouter>
                <Switch>
                  <Route exact path="/test" component={TestPage} />

                  <Route exact path="/start" component={StartPage} />

                  <Route exact path="/init" component={StartSessionPage} />

                  <Route
                    exact
                    path="/select-language"
                    component={SelectLanguagePage}
                  />

                  <Route exact path="/get-ready" component={GetReadyPage} />

                  <Route
                    exact
                    path="/select-pickup-details"
                    component={SelectPickupDetailsPage}
                  />

                  <Route
                    exact
                    path="/input-reservation-ref"
                    component={InputReservationReferencePage}
                  />
                  <Route
                    exact
                    path="/input-reservation-ref-error"
                    component={InputReservationReferenceErrorPage}
                  />

                  <Route
                    exact
                    path="/review-reservation"
                    component={ReviewReservationPage}
                  />
                  <Route
                    exact
                    path="/review-reservation-extras"
                    component={ReviewReservationExtrasPage}
                  />

                  <Route
                    exact
                    path="/driver-info-step/1"
                    component={DriverStep1Page}
                  />
                  <Route
                    exact
                    path="/driver-info-step/2"
                    component={DriverStep2Page}
                  />
                  <Route
                    exact
                    path="/driver-info-step/3"
                    component={DriverStep3Page}
                  />
                  <Route
                    exact
                    path="/driver-info-invalid-address"
                    component={DriverInfoInvalidAddressPage}
                  />
                  <Route
                    exact
                    path="/driver-info-review"
                    component={DriverInfoReviewPage}
                  />

                  <Route
                    exact
                    path="/additional-driver-info-step/1"
                    component={AdditionalDriverStepPage}
                  />
                  <Route
                    exact
                    path="/additional-driver-info-review"
                    component={AdditionalDriverInfoReview}
                  />

                  <Route exact path="/baby-seats" component={BabySeatsPage} />

                  <Route
                    exact
                    path="/questions/:id"
                    component={ValidateSessionId(QuestionPage)}
                  />

                  <Route
                    exact
                    path="/comfort-extras"
                    component={ComfortExtrasPage}
                  />

                  <Route exact path="/single-extra/:id" component={Template1Page} />

                  <Route exact path="/upselling" component={UpsellingPage} />

                  <Route exact path="/safety-packs" component={SafetyPacksPage} />
                  <Route
                    exact
                    path="/safety-packs/:id"
                    component={SafetyPackDetailedPage}
                  />

                  <Route
                    exact
                    path="/review-final-reservation"
                    component={ReviewFinalReservationPage}
                  />
                  <Route
                    exact
                    path="/review-final-reservation-extras"
                    component={ReviewFinalReservationExtrasPage}
                  />

                  <Route exact path="/thank-you" component={ThankYouPage} />

                  <Route
                    exact
                    path="/reservation"
                    component={AutoSearchReservationPage}
                  />
                  <Route
                    exact
                    path="/reservation-menu"
                    component={SecondTryCheckInPage}
                  />

                  <Route
                    exact
                    path="/terms-conditions"
                    component={ValidateSessionId(TermsConditionsPage)}
                  />

                  <Route exact path="/rating" component={RatingPage} />

                  <Route
                    exact
                    path="/exclusive-reservation"
                    component={ValidateSessionId(ExclusiveReservationQuestionPage)}
                  />

                  <Route exact path={PUSH_SALES_PATH} component={PushSalesPage} />

                  <Route
                    exact
                    path="/review-rental-agreement"
                    component={ReviewRentalAgreementPage}
                  />

                  <Route
                    exact
                    path="/rental-agreement-expired"
                    component={ExpiredRentalAgreementPage}
                  />

                  <Route exact path="/payments" component={PaymentsPage} />
                  <Route
                    exact
                    path="/payments-error"
                    component={PaymentsErrorPage}
                  />
                  <Route
                    exact
                    path="/payments-success"
                    component={PaymentsSuccessPage}
                  />
                  <Route
                    exact
                    path="/payments/reminder"
                    component={PaymentsReminderPage}
                  />

                  <Route exact path="/keyless" component={KeylessPage} />
                  <Route exact path="/keyless/confirm-self-checkout" component={KeylessSelfCheckoutConfirmPage} />

                  <Route exact path="/convention" component={ConventionPage} />

                  <Redirect from="*" to={"/start" + queryStringParams} />
                </Switch>
              </BrowserRouter>
              <AlertModalContainer />
            </DomainRepository>
          </GroupsImagesRepository>
        </ErrorBoundry>
      </QueryClientProvider>
    </DeviceDetector>
  );
}

export default Root;
